import React, { useState, useEffect } from 'react';
import {
  MapPin,
  Clock,
  Star,
  ChevronRight,
  Eye,
  Navigation,
  Sparkles,
} from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';

const WalkHighlights = ({
  walkId,
  walkCoordinates,
  regularHighlights = [],
  savedCulturalPOIs = [],
  onPOISelect,
  onTagClick = () => {},
  title = "Walk Highlights"
}) => {
  const [culturalPOIs, setCulturalPOIs] = useState(savedCulturalPOIs);
  const [loading, setLoading] = useState(false);
  const [showAllHighlights, setShowAllHighlights] = useState(false);

  useEffect(() => {
    if (savedCulturalPOIs && savedCulturalPOIs.length > 0) {
      setCulturalPOIs(savedCulturalPOIs);
      setLoading(false);
      return;
    }
    if (walkId || walkCoordinates) {
      fetchCulturalPOIs();
    } else {
      setLoading(false);
    }
  }, [walkId, walkCoordinates, savedCulturalPOIs]);

  const fetchCulturalPOIs = async () => {
    try {
      setLoading(true);
      let response;
      if (walkId) {
        try {
          response = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/walks/${walkId}/cultural-pois`
          );
        } catch {
          if (walkCoordinates && walkCoordinates.length > 0) {
            const c = walkCoordinates[Math.floor(walkCoordinates.length / 2)];
            try {
              response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/cultural-pois/search`,
                { params: { lat: c.lat, lng: c.lng, radius: 500, limit: 20 } }
              );
              setCulturalPOIs(response.data || []);
              return;
            } catch {
              setCulturalPOIs([]);
              return;
            }
          }
          setCulturalPOIs([]);
          return;
        }
      } else if (walkCoordinates && walkCoordinates.length > 0) {
        const c = walkCoordinates[Math.floor(walkCoordinates.length / 2)];
        response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/cultural-pois/search`,
          { params: { lat: c.lat, lng: c.lng, radius: 500, limit: 20 } }
        );
        setCulturalPOIs(response.data || []);
        return;
      }
      if (response && response.data) {
        setCulturalPOIs(response.data.cultural_pois || []);
      }
    } catch {
      setCulturalPOIs([]);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'food': return '🍕';
      case 'art': return '🎨';
      case 'architecture': return '🏛️';
      case 'business': return '🛍️';
      case 'museum': return '🏛️';
      case 'viewpoint': return '🌆';
      case 'cultural_venue': return '🎭';
      case 'hidden_gem': return '💎';
      case 'shop': return '🛍️';
      default: return '📍';
    }
  };

  const getCategoryLabel = (category) => {
    const labels = {
      food: 'Food & Drink',
      art: 'Art',
      architecture: 'Architecture',
      business: 'Local Business',
      museum: 'Museum',
      viewpoint: 'Viewpoint',
      cultural_venue: 'Cultural Venue',
      hidden_gem: 'Hidden Gem',
    };
    return labels[category] || 'Point of Interest';
  };

  // Combine regular highlights with cultural POIs
  const allHighlights = [
    ...regularHighlights.filter(Boolean).map((highlight, index) => ({
      id: `regular-${index}`,
      type: 'regular',
      title: typeof highlight === 'string' ? highlight : (highlight.title || ''),
      description: typeof highlight === 'string' ? '' : (highlight.description || ''),
      icon: '📍',
      coords: typeof highlight === 'string' ? null : (highlight.coords || null),
    })),
    ...culturalPOIs.slice(0, 8).map((poi, index) => ({
      id: poi.id || poi.place_id || `cultural-${index}`,
      type: 'cultural',
      title: poi.name || poi.title || '',
      description: poi.description || poi.formatted_address || '',
      icon: getCategoryIcon(poi.category),
      category: poi.category,
      subcategory: poi.subcategory,
      cultural_significance: poi.cultural_significance,
      insider_tips: poi.insider_tips,
      user_ratings: poi.user_ratings,
      price_range: poi.price_range,
      estimated_visit_duration: poi.estimated_visit_duration,
      website: poi.website,
      tags: poi.tags || [],
      poi: poi
    }))
  ];

  const visibleHighlights = showAllHighlights ? allHighlights : allHighlights.slice(0, 6);
  const hasMoreHighlights = allHighlights.length > 6;

  const handleCardClick = (highlight) => {
    if (!onPOISelect) return;
    if (highlight.type === 'cultural') {
      onPOISelect(highlight.poi);
    } else {
      onPOISelect({
        id: highlight.id,
        name: highlight.title,
        description: highlight.description,
        category: 'highlight',
        coordinates: { lat: highlight.coords?.lat, lng: highlight.coords?.lng },
        type: 'highlight',
      });
    }
  };

  // ── Loading skeleton ────────────────────────────────────────────
  if (loading) {
    return (
      <section>
        <SectionHeader title={title} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="animate-pulse rounded-xl border border-gray-100 p-5">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-gray-200 rounded-full flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-2/3" />
                  <div className="h-3 bg-gray-200 rounded w-full" />
                  <div className="h-3 bg-gray-200 rounded w-1/2" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  // ── Empty state ─────────────────────────────────────────────────
  if (allHighlights.length === 0) {
    return (
      <section>
        <SectionHeader title={title} />
        <div className="text-center py-12 text-gray-400">
          <MapPin className="h-10 w-10 mx-auto mb-3 opacity-40" />
          <p className="text-sm">No highlights available for this walk yet</p>
        </div>
      </section>
    );
  }

  // ── Main render ─────────────────────────────────────────────────
  return (
    <section>
      <SectionHeader
        title={title}
        count={allHighlights.length}
        hasMore={hasMoreHighlights}
        showAll={showAllHighlights}
        onToggle={() => setShowAllHighlights(!showAllHighlights)}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
        {visibleHighlights.map((highlight, index) => (
          <HighlightCard
            key={highlight.id}
            highlight={highlight}
            stopNumber={index + 1}
            onClick={() => handleCardClick(highlight)}
            onTagClick={onTagClick}
          />
        ))}
      </div>
    </section>
  );
};


// ── Section Header ────────────────────────────────────────────────

const SectionHeader = ({ title, count, hasMore, showAll, onToggle }) => (
  <div className="flex items-end justify-between gap-4">
    <div>
      <h2 className="text-2xl font-bold text-gray-900 tracking-tight">{title}</h2>
      <p className="text-gray-500 mt-1 text-[15px] leading-relaxed">
        Discover local culture, hidden gems, and must-see stops along your route
      </p>
    </div>
    {hasMore && (
      <Button
        size="sm"
        variant="outline"
        onClick={onToggle}
        className="flex-shrink-0 text-sm"
      >
        <Eye className="h-4 w-4 mr-1.5" />
        {showAll ? 'Show Less' : `View All (${count})`}
      </Button>
    )}
  </div>
);


// ── Highlight Card ────────────────────────────────────────────────

const HighlightCard = ({ highlight, stopNumber, onClick, onTagClick }) => {
  const isCultural = highlight.type === 'cultural';

  return (
    <div
      onClick={onClick}
      className={`
        group relative rounded-xl border bg-white p-5
        shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer
        ${isCultural
          ? 'border-amber-200/70 hover:border-amber-300'
          : 'border-gray-200 hover:border-purple-300'}
      `}
    >
      {/* Top row: number/icon + title + badge */}
      <div className="flex items-start gap-3.5">
        {/* Stop number */}
        <div className="flex-shrink-0 w-9 h-9 rounded-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center shadow-sm">
          <span className="text-white text-sm font-bold">{stopNumber}</span>
        </div>

        <div className="flex-1 min-w-0">
          {/* Title + badge row */}
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-semibold text-gray-900 leading-snug group-hover:text-gray-700 transition-colors">
              {highlight.title}
            </h3>
            {isCultural ? (
              <span className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-800">
                <Sparkles className="w-3 h-3" />
                {getCategoryLabelStatic(highlight.category)}
              </span>
            ) : (
              <span className="flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-purple-100 text-purple-700">
                Highlight
              </span>
            )}
          </div>

          {/* Description — full text, no truncation */}
          {highlight.description && (
            <p className="text-gray-600 text-sm leading-relaxed mt-1.5">
              {highlight.description}
            </p>
          )}

          {/* Cultural POI extras */}
          {isCultural && (highlight.cultural_significance || highlight.insider_tips) && (
            <div className="mt-3 space-y-2">
              {highlight.cultural_significance && (
                <p className="text-sm text-blue-800 bg-blue-50 rounded-lg px-3 py-2 leading-relaxed">
                  <span className="font-medium">Why it matters:</span>{' '}
                  {highlight.cultural_significance}
                </p>
              )}
              {highlight.insider_tips && (
                <p className="text-sm text-emerald-800 bg-emerald-50 rounded-lg px-3 py-2 leading-relaxed">
                  <span className="font-medium">Local tip:</span>{' '}
                  {highlight.insider_tips}
                </p>
              )}
            </div>
          )}

          {/* Meta row: rating, duration, tags */}
          {isCultural && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-xs text-gray-500">
              {highlight.user_ratings >= 4.0 && (
                <span className="inline-flex items-center gap-1">
                  <Star className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" />
                  <span className="font-medium text-gray-700">{highlight.user_ratings.toFixed(1)}</span>
                </span>
              )}
              {highlight.estimated_visit_duration && (
                <span className="inline-flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5" />
                  {highlight.estimated_visit_duration} min
                </span>
              )}
              {highlight.price_range && (
                <span>{highlight.price_range}</span>
              )}
            </div>
          )}

          {/* Tags */}
          {highlight.tags && highlight.tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2.5">
              {highlight.tags.slice(0, 4).map((tag, i) => (
                <button
                  key={i}
                  onClick={(e) => { e.stopPropagation(); onTagClick(tag); }}
                  className="px-2 py-0.5 bg-gray-100 hover:bg-blue-100 text-gray-500 hover:text-blue-600 text-[11px] rounded-md transition-colors"
                >
                  #{tag}
                </button>
              ))}
              {highlight.tags.length > 4 && (
                <span className="px-2 py-0.5 text-gray-400 text-[11px]">
                  +{highlight.tags.length - 4}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Chevron */}
        <ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-gray-500 flex-shrink-0 mt-1 transition-colors" />
      </div>
    </div>
  );
};

// Static helper (avoids re-defining inside render)
function getCategoryLabelStatic(category) {
  const labels = {
    food: 'Food & Drink',
    art: 'Art',
    architecture: 'Architecture',
    business: 'Local Business',
    museum: 'Museum',
    viewpoint: 'Viewpoint',
    cultural_venue: 'Cultural Venue',
    hidden_gem: 'Hidden Gem',
    shop: 'Shop',
  };
  return labels[category] || 'Cultural';
}

export default WalkHighlights;
